import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Order, OrderItem } from '../../types';
import * as orderService from '../../services/order-service';
import { RootState } from '../store';

interface OrderState {
  items: Order[];
  selectedOrder: Order | null;
  orderItems: OrderItem[];
  loading: boolean;
  error: string | null;
}

const initialState: OrderState = {
  items: [],
  selectedOrder: null,
  orderItems: [],
  loading: false,
  error: null,
};

export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await orderService.getOrders();
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue('Failed to fetch orders');
      }
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const searchOrders = createAsyncThunk(
  'orders/searchOrders',
  async (searchTerm: string, { rejectWithValue }) => {
    try {
      const response = await orderService.searchOrders(searchTerm);
      if (!response.success) {
        throw new Error('Failed to search orders');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await orderService.getOrderById(orderId);
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue('Failed to fetch order details');
      }
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchOrderItems = createAsyncThunk(
  'orders/fetchOrderItems',
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await orderService.getOrderItems(orderId);
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue('Failed to fetch order items');
      }
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    selectOrder: (state, action: PayloadAction<Order>) => {
      state.selectedOrder = action.payload;
    },
    clearSelectedOrder: state => {
      state.selectedOrder = null;
      state.orderItems = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchOrders.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchOrderById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrder = action.payload;
        state.items = state.items.map(order =>
          order.id === action.payload.id ? action.payload : order
        );
        // If the order doesn't exist in items, add it
        if (!state.items.some(order => order.id === action.payload.id)) {
          state.items.push(action.payload);
        }
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchOrderItems.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderItems.fulfilled, (state, action) => {
        state.loading = false;
        state.orderItems = action.payload;
      })
      .addCase(fetchOrderItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Selectors
export const selectOrders = (state: RootState) => state.orders.items;
export const selectSelectedOrder = (state: RootState) => state.orders.selectedOrder;
export const selectOrderItems = (state: RootState) => state.orders.orderItems;
export const selectLoading = (state: RootState) => state.orders.loading;
export const selectError = (state: RootState) => state.orders.error;

export const { selectOrder, clearSelectedOrder } = orderSlice.actions;
export default orderSlice.reducer;
